import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  XIcon,
  CheckCircle2Icon,
  XCircleIcon,
  Loader2Icon,
  ClockIcon,
  AlertTriangleIcon,
} from "lucide-react";

import Message from "../../../../../i18n/Message";
import { builderSelectors } from "../../../store/builderReducer";
import { loadProjectEvents } from "../../../store/builderActionCreators";

import "./ProjectEventsOverlay.scss";

const STATUS_ICONS = {
  SUCCESS: <CheckCircle2Icon size={16} className="status_icon success" />,
  FAILED: <XCircleIcon size={16} className="status_icon failed" />,
  RUNNING: <Loader2Icon size={16} className="status_icon running spinning" />,
  PENDING: <ClockIcon size={16} className="status_icon pending" />,
};

const formatRelativeTime = (date) => {
  if (!date) return "";
  const now = new Date();
  const eventDate = new Date(date);
  const diffMs = now - eventDate;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
  return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
};

const EventRow = ({ event }) => {
  const statusIcon = STATUS_ICONS[event.status] || STATUS_ICONS.PENDING;

  return (
    <div className={`event_row ${event.status.toLowerCase()}`}>
      <div className="event_main">
        {statusIcon}
        <span className="event_description">{event.actionDescription}</span>
        <span className="event_time">{formatRelativeTime(event.dateCreated)}</span>
      </div>
      {event.status === "FAILED" && event.errorMessage && (
        <div className="event_error">
          <span className="error_label">Error:</span>
          <span className="error_message">{event.errorMessage}</span>
        </div>
      )}
      {event.status === "SUCCESS" && event.warningMessage && (
        <div className="event_warning">
          <AlertTriangleIcon size={14} />
          <span className="warning_message">{event.warningMessage}</span>
        </div>
      )}
    </div>
  );
};

const ProjectEventsOverlay = ({ projectUid, onClose }) => {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const events = useSelector(builderSelectors.getProjectEvents);
  const isLoading = useSelector(builderSelectors.isProjectEventsLoading);

  useEffect(() => {
    if (workspaceId && projectUid) {
      dispatch(loadProjectEvents(workspaceId, projectUid));
    }
  }, [dispatch, workspaceId, projectUid]);

  return (
    <div className="project_events_overlay">
      <div className="overlay_backdrop" onClick={onClose} />
      <div className="overlay_panel">
        <div className="overlay_header">
          <h3 className="overlay_title">
            <Message id="builder_project_events" defaultMessage="Project Events" />
          </h3>
          <button type="button" className="close_button" onClick={onClose}>
            <XIcon size={18} />
          </button>
        </div>
        <div className="overlay_content">
          {isLoading && (
            <div className="loading_state">
              <Loader2Icon size={24} className="spinning" />
              <span>
                <Message id="loading" defaultMessage="Loading..." />
              </span>
            </div>
          )}
          {!isLoading && events.length === 0 && (
            <div className="empty_state">
              <Message id="builder_no_events" defaultMessage="No events yet" />
            </div>
          )}
          {!isLoading && events.length > 0 && (
            <div className="events_list">
              {events.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectEventsOverlay;
